/**
 * PHOTO CAPTURE COMPONENT - NATIVE CAMERA
 *
 * Uses native camera app for best mobile UX:
 * - Triggers native camera on mobile devices
 * - Full-screen camera experience users expect
 * - All native camera features (flash, zoom, focus)
 * - Falls back to file picker on desktop
 * - Shows preview after capture with retake option
 * - Client-side image compression before upload
 */

import { useRef, useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'

type Props = {
  submissionId: string
  writeToken?: string | null
  side?: 'left' | 'right'
  position?: string
  existingMedia?: boolean | string
  accountHash?: string
  onPhotoSaved?: (cloudflareId?: string) => void
  onSaved?: (cloudflareId?: string) => void
}

type AssetMap = Record<string, { url?: string }>
type AssetsResponse = { assets?: AssetMap }
type UploadResponse = { cloudflareId?: string; error?: string }

async function compressImage(file: File, maxWidth = 1920): Promise<Blob> {
  return new Promise((resolve) => {
    const img = new Image()
    const objectUrl = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(objectUrl)
      const canvas = document.createElement('canvas')
      const scale = Math.min(1, maxWidth / img.width)
      canvas.width = img.width * scale
      canvas.height = img.height * scale
      const ctx = canvas.getContext('2d')!
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      canvas.toBlob((blob) => resolve(blob!), 'image/jpeg', 0.85)
    }
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      resolve(file)
    }
    img.src = objectUrl
  })
}

export default function PhotoCapture({ submissionId, writeToken, side, position, existingMedia, accountHash, onPhotoSaved, onSaved }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const previewRef = useRef<HTMLDivElement>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)

  // Track if user has manually retaken (to prevent auto-restore after retake)
  const [hasRetaken, setHasRetaken] = useState(false)

  const emitSaved = (cloudflareId?: string) => {
    onPhotoSaved?.(cloudflareId)
    onSaved?.(cloudflareId)
  }

  // Scroll preview into center view after capture
  const scrollToPreview = () => {
    setTimeout(() => {
      previewRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }, 100)
  }

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      if (preview && preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview)
      }
    }
  }, [preview])

  // Handle existing media on component mount (but not after retake)
  useEffect(() => {
    const loadExistingMedia = async () => {
      if (existingMedia && !preview && !hasRetaken) {
        // In development mode, try to restore preview from localStorage
        if (window.location.hostname === 'localhost') {
          const localKey = `photo-${submissionId}-${side}`
          const localPreview = localStorage.getItem(localKey)
          if (localPreview) {
            setPreview(localPreview)
            const mockFile = new File(['mock'], `${side}-${position}.jpg`, { type: 'image/jpeg' })
            setFile(mockFile)
            return
          }
        }

        // If existingMedia is a Cloudflare Image ID string, construct URL client-side
        if (typeof existingMedia === 'string' && existingMedia !== 'true') {
          const hash = import.meta.env.VITE_IMAGES_ACCOUNT_HASH || accountHash
          if (hash) {
            setPreview(`https://imagedelivery.net/${hash}/${existingMedia}/public`)
            return
          }
        }

        // For production, fetch the actual image URL from the database
        try {
          const headers: Record<string, string> = {}
          if (writeToken) {
            headers['Authorization'] = `Bearer ${writeToken}`
          }
          const response = await fetch(`/api/assets/${submissionId}`, { headers })
          if (response.ok) {
            const data = await response.json() as AssetsResponse
            const mediaKey = `photo_${side}_${position}`
            const asset = data.assets?.[mediaKey]

            if (asset && asset.url) {
              setPreview(asset.url)
              return
            }
          }
        } catch (error) {
          Sentry.captureException(error, { tags: { action: 'fetch_photo_url', side, position } })
        }

        // Fallback to placeholder
        setPreview('existing-photo-placeholder')
      }
    }

    loadExistingMedia()
  }, [existingMedia, submissionId, side, position, preview, hasRetaken, accountHash])

  // Handle photo capture from native camera
  const handlePhotoCapture = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) return

    setUploadError(null)

    // Create preview URL
    const previewUrl = URL.createObjectURL(selectedFile)
    setPreview(previewUrl)
    setFile(selectedFile)
    scrollToPreview()

    // Start upload process
    await handleUpload(selectedFile)
  }

  // Handle file upload
  const handleUpload = async (fileToUpload: File) => {
    setUploading(true)
    setUploadError(null)

    // Development mode - skip actual upload
    if (window.location.hostname === 'localhost') {
      await new Promise(resolve => setTimeout(resolve, 1500))
      setUploading(false)
      emitSaved()
      return
    }

    try {
      // Compress image before upload
      const compressed = await compressImage(fileToUpload)

      const formData = new FormData()
      formData.append('file', compressed, fileToUpload.name)
      formData.append('submissionId', submissionId)
      formData.append('side', side || '')
      formData.append('position', position || '')

      const headers: Record<string, string> = {}
      if (writeToken) {
        headers['Authorization'] = `Bearer ${writeToken}`
      }

      const uploadRes = await fetch('/api/uploads/image', {
        method: 'POST',
        headers,
        body: formData
      })

      if (!uploadRes.ok) {
        const errorData = await uploadRes.json().catch(() => ({} as UploadResponse)) as UploadResponse
        throw new Error(errorData.error || `Upload failed: ${uploadRes.status}`)
      }

      const result = await uploadRes.json() as UploadResponse
      emitSaved(result.cloudflareId)
    } catch (error) {
      Sentry.captureException(error, { tags: { action: 'photo_upload', side, position } })
      setUploadError(error instanceof Error ? error.message : 'Upload failed. Please retake the photo.')
    } finally {
      setUploading(false)
    }
  }

  // Trigger native camera
  const takePhoto = () => {
    fileInputRef.current?.click()
  }

  // Reset to take another photo
  const retakePhoto = () => {
    if (preview && preview.startsWith('blob:')) {
      URL.revokeObjectURL(preview)
    }
    setPreview(null)
    setFile(null)
    setUploadError(null)
    setHasRetaken(true)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className="space-y-3">
      {/* Hidden file input that triggers native camera */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handlePhotoCapture}
        className="hidden"
      />

      {!preview ? (
        // Capture State: Show button to trigger native camera
        <div className="space-y-3">
          <button
            onClick={takePhoto}
            disabled={uploading}
            className="w-full bg-green-500 text-white py-4 px-6 rounded-lg font-medium text-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? 'Processing...' : `Take ${side ? side + ' hand ' : ''}photo`}
          </button>
        </div>
      ) : (
        // Preview State: Show captured photo with options
        <div className="space-y-3">
          {/* Photo Preview */}
          <div ref={previewRef} className="relative">
            {preview === 'existing-photo-placeholder' ? (
              <div className="w-full bg-green-50 rounded-lg border-2 border-green-200 flex flex-col items-center justify-center text-green-700 p-6" style={{ aspectRatio: '3/4', maxHeight: '40vh' }}>
                <div className="text-4xl mb-2">&#x1f4f8;</div>
                <div className="text-center">
                  <div className="font-medium">Photo Captured</div>
                  <div className="text-sm mt-1">Previously saved to database</div>
                  <div className="text-xs text-green-600 mt-1">Click Retake to replace</div>
                </div>
              </div>
            ) : (
              <div className="w-full rounded-lg border-2 border-gray-200 overflow-hidden" style={{ aspectRatio: '3/4', maxHeight: '40vh' }}>
                <img
                  src={preview}
                  alt={`Captured ${side || ''} hand photo`}
                  className="w-full h-full object-contain"
                  style={{ imageOrientation: 'from-image' }}
                  onError={() => setPreview('existing-photo-placeholder')}
                />
              </div>
            )}
            {uploading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                <div className="bg-white px-4 py-2 rounded-lg flex items-center space-x-2">
                  <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                  <span className="text-sm font-medium">Saving photo...</span>
                </div>
              </div>
            )}
          </div>

          {/* Upload error message */}
          {uploadError && (
            <div className="text-red-500 text-sm text-center p-2 bg-red-50 rounded-lg">
              {uploadError}
            </div>
          )}

          {/* Action Button */}
          <div className="flex justify-center">
            <button
              onClick={retakePhoto}
              disabled={uploading}
              className="bg-gray-500 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              Retake
            </button>
          </div>

          {/* File Info (Development) */}
          {file && window.location.hostname === 'localhost' && (
            <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
              <strong>File:</strong> {file.name} ({Math.round(file.size / 1024)}KB)
            </div>
          )}
        </div>
      )}
    </div>
  )
}
